import React, { useEffect, useRef, useState } from 'react';
import {
  CpuChipIcon,
  BoltIcon,
  DocumentArrowUpIcon,
  ShieldCheckIcon,
  ClockIcon,
  StarIcon,
} from '@heroicons/react/24/outline';

interface Advantage {
  icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  title: string;
  description: string;
  color: string;
}

const ADVANTAGES: Advantage[] = [
  {
    icon: CpuChipIcon,
    title: 'AI 深度分析',
    description: '基于大型语言模型，深度理解招标文件中的复杂条款，准确提取关键信息，精准识别评标要点',
    color: '#3b82f6',
  },
  {
    icon: BoltIcon,
    title: '实时流式生成',
    description: '采用流式响应技术，内容生成实时可见，进度透明，大幅缩短等待时间，提升使用体验',
    color: '#f59e0b',
  },
  {
    icon: DocumentArrowUpIcon,
    title: '多格式支持',
    description: '支持 Word (.docx) 与 PDF 格式招标文件上传，智能解析，最终生成标准 Word 文档',
    color: '#14b8a6',
  },
  {
    icon: ShieldCheckIcon,
    title: '全面覆盖评分',
    description: '智能比对技术评分要求，确保标书内容覆盖所有评标指标，最大化得分潜力，降低失分风险',
    color: '#22c55e',
  },
  {
    icon: ClockIcon,
    title: '效率大幅提升',
    description: '传统人工编写需要数天，AI 辅助系统将整体流程压缩至数小时，效率提升数倍',
    color: '#06b6d4',
  },
  {
    icon: StarIcon,
    title: '专业输出质量',
    description: '结合行业模板与 AI 能力，输出结构清晰、内容专业、格式规范的高质量投标文件',
    color: '#f97316',
  },
];

const TechSection: React.FC = () => {
  const [visible, setVisible] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section
      id="tech"
      className="py-28"
      ref={sectionRef}
      style={{ background: 'linear-gradient(135deg, #0f172a 0%, #1e2d4a 50%, #0f172a 100%)' }}
    >
      <div className="max-w-6xl mx-auto px-6">
        <div className="text-center mb-16">
          <div
            className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border mb-6"
            style={{
              background: 'rgba(59, 130, 246, 0.1)',
              borderColor: 'rgba(59, 130, 246, 0.3)',
            }}
          >
            <CpuChipIcon className="w-3.5 h-3.5 text-blue-400" />
            <span className="text-sm font-semibold text-blue-300">AI 技术能力</span>
          </div>
          <h2
            className="text-4xl md:text-5xl font-black text-white mb-6 tracking-tight transition-all duration-700"
            style={{
              opacity: visible ? 1 : 0,
              transform: visible ? 'translateY(0)' : 'translateY(16px)',
            }}
          >
            先进技术，驱动卓越结果
          </h2>
          <p
            className="text-xl text-slate-400 max-w-2xl mx-auto leading-relaxed transition-all duration-700"
            style={{
              opacity: visible ? 1 : 0,
              transform: visible ? 'translateY(0)' : 'translateY(16px)',
              transitionDelay: '100ms',
            }}
          >
            融合前沿 AI 技术与行业实践，为标书编写提供全方位智能支撑
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {ADVANTAGES.map((adv, i) => {
            const Icon = adv.icon;
            return (
              <div
                key={adv.title}
                className="rounded-2xl p-6 border backdrop-blur-sm group hover:border-white/25 transition-all duration-300"
                style={{
                  background: 'rgba(255,255,255,0.04)',
                  borderColor: 'rgba(255,255,255,0.1)',
                  opacity: visible ? 1 : 0,
                  transform: visible ? 'translateY(0)' : 'translateY(20px)',
                  transition: `opacity 0.6s ease ${i * 80}ms, transform 0.6s ease ${i * 80}ms, border-color 0.3s ease`,
                }}
              >
                <div
                  className="w-12 h-12 rounded-2xl flex items-center justify-center mb-5 transition-transform duration-300 group-hover:scale-110"
                  style={{
                    background: adv.color + '1a',
                    border: `1px solid ${adv.color}40`,
                  }}
                >
                  <Icon className="w-6 h-6" style={{ color: adv.color }} />
                </div>
                <h3 className="text-lg font-bold text-white mb-2.5">{adv.title}</h3>
                <p className="text-slate-400 text-sm leading-relaxed">{adv.description}</p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default TechSection;
